import { LifeSimulator } from "../app/simulator";
import { NaiveLifeEngine } from "../engine/naive-engine";
import { FEATURED_PRESETS, type LifePreset } from "../patterns/presets";
import { CanvasLifeRenderer } from "./canvas-view";

const DEFAULT_CENTER = 1 / 3;
const LAST_PRESET_KEY = "portfolio-life:last-preset";

const TEMPLATE = `
  <style>
    :host {
      --life-background-color: oklch(96% 0.002 17.2);
      --life-alive-color: oklch(14.7% 0.004 49.3);
      --life-grid-color: oklch(14.7% 0.004 49.3 / 4.5%);
      --life-accent-color: oklch(64.6% 0.222 41.116);
      --life-surface-color: oklch(96% 0.002 17.2 / 88%);
      --life-text-color: oklch(14.7% 0.004 49.3);
      --life-border-color: oklch(14.7% 0.004 49.3 / 16%);
      --life-canvas-opacity: 1;
      --life-font: "DM Mono", monospace;

      position: absolute;
      inset: 0;
      display: block;
      min-width: 1px;
      min-height: 1px;
      overflow: hidden;
      contain: layout paint style;
      color: var(--life-text-color);
      font: 0.68rem var(--life-font);
      pointer-events: none;
    }

    canvas {
      position: absolute;
      inset: 0;
      display: block;
      width: 100%;
      height: 100%;
      opacity: var(--life-canvas-opacity);
      pointer-events: none;
    }

    :host([interactive]) canvas {
      cursor: crosshair;
      pointer-events: auto;
      touch-action: none;
    }

    .controls {
      position: absolute;
      top: 50%;
      left: clamp(0.5rem, 2.5vw, 1rem);
      display: none;
      transform: translateY(-50%);
      pointer-events: auto;
    }

    :host([controls]) .controls { display: block; }

    .controls,
    .popover {
      border: 1px solid var(--life-border-color);
      background: var(--life-surface-color);
      backdrop-filter: blur(4px);
    }

    :is(button, a) {
      display: grid;
      width: 1.75rem;
      min-height: 1.75rem;
      place-items: center;
      border: 0;
      color: inherit;
      background: none;
      font: inherit;
      text-decoration: none;
      cursor: pointer;
    }

    :is(button, a):is(:hover, :focus-visible),
    .control:hover > .speed-toggle {
      color: var(--life-accent-color);
    }

    .material-symbols-outlined {
      font-family: "Material Symbols Outlined";
      font-size: 0.875rem;
      font-feature-settings: "liga";
      line-height: 1;
    }

    .control { position: relative; }

    .control::after {
      content: "";
      position: absolute;
      z-index: 1;
      top: 0;
      left: 100%;
      width: 0.75rem;
      height: 100%;
    }

    .popover {
      position: absolute;
      z-index: 1;
      top: 50%;
      left: calc(100% + 0.75rem);
      width: max-content;
      max-width: min(30rem, calc(100vw - 5.5rem));
      padding: 0.65rem 0.75rem;
      color: var(--life-text-color);
      line-height: 1.45;
      transform: translateY(-50%);
    }

    .speed-panel { display: none; }

    .control:hover > .speed-panel {
      display: block;
    }

    .speed-panel label {
      display: flex;
      align-items: center;
      gap: 0.6rem;
    }

    .speed-input {
      width: clamp(5.5rem, 16vw, 7rem);
      accent-color: var(--life-accent-color);
    }

    .speed-value {
      width: 2rem;
      font-variant-numeric: tabular-nums;
    }

    .info {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      max-width: 24rem;
      text-align: left;
      visibility: hidden;
    }

    .info-heading { white-space: nowrap; }
    .info-description,
    .info-cta { opacity: 0.68; }
    .control:is(:hover, :focus-within) > .info { visibility: visible; }

  </style>
  <canvas id="canvas" aria-hidden="true"></canvas>
  <div class="controls" role="toolbar" aria-label="Simulation controls">
    <button id="play" class="control" type="button" aria-pressed="false" aria-label="Play" title="Play">
      <span id="play-icon" class="material-symbols-outlined" aria-hidden="true">play_arrow</span>
    </button>
    <div class="control">
      <button class="speed-toggle" type="button" aria-label="Adjust simulation speed" title="Adjust speed">
        <span class="material-symbols-outlined" aria-hidden="true">speed</span>
      </button>
      <div class="popover speed-panel">
        <label>
          <input id="speed" class="speed-input" type="range" min="1" max="30" value="8" aria-label="Generations per second">
          <output id="speed-value" class="speed-value" for="speed">8/s</output>
        </label>
      </div>
    </div>
    <button id="clear" type="button" aria-label="Clear" title="Clear">
      <span class="material-symbols-outlined" aria-hidden="true">close</span>
    </button>
    <button id="expand" type="button" aria-expanded="false" aria-label="Expand" title="Expand">
      <span id="expand-icon" class="material-symbols-outlined" aria-hidden="true">fullscreen</span>
    </button>
    <a class="control" href="/game-of-life" aria-label="Learn more">
      <span class="material-symbols-outlined" aria-hidden="true">info</span>
      <span class="popover info" role="tooltip">
        <span class="info-heading">
          <strong id="learn-more-name">Pattern</strong><span id="learn-more-attribution">, Creator, Year</span>
        </span>
        <span id="learn-more-description" class="info-description">Conway's Game of Life</span>
        <em class="info-cta">Click to learn more. Reload to discover a new pattern.</em>
      </span>
    </a>
  </div>
`;

function componentElements(shadow: ShadowRoot) {
  return {
    canvas: shadow.getElementById("canvas") as HTMLCanvasElement,
    playButton: shadow.getElementById("play") as HTMLButtonElement,
    playIcon: shadow.getElementById("play-icon")!,
    speedInput: shadow.getElementById("speed") as HTMLInputElement,
    speedValue: shadow.getElementById("speed-value")!,
    clearButton: shadow.getElementById("clear") as HTMLButtonElement,
    expandButton: shadow.getElementById("expand") as HTMLButtonElement,
    expandIcon: shadow.getElementById("expand-icon")!,
    learnMoreName: shadow.getElementById("learn-more-name")!,
    learnMoreAttribution: shadow.getElementById("learn-more-attribution")!,
    learnMoreDescription: shadow.getElementById("learn-more-description")!,
  };
}

export class GameOfLifeElement extends HTMLElement {
  static readonly observedAttributes = ["center"];

  private simulator: LifeSimulator | undefined;
  private renderer: CanvasLifeRenderer | undefined;
  private readonly elements: ReturnType<typeof componentElements>;
  private readonly resizeObserver: ResizeObserver;
  private animationFrame = 0;
  private previousFrameAt: number | undefined;
  private drawState: boolean | null = null;
  private expanded = false;
  private cameraFocus = { x: 0, y: 0 };

  constructor() {
    super();
    const shadow = this.attachShadow({ mode: "open" });
    shadow.innerHTML = TEMPLATE;
    this.elements = componentElements(shadow);
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.bindControls();
  }

  connectedCallback(): void {
    const { elements } = this;
    const styles = getComputedStyle(this);
    const color = (property: string) =>
      styles.getPropertyValue(property).trim();
    const renderer = new CanvasLifeRenderer(
      elements.canvas,
      {
        background: color("--life-background-color"),
        alive: color("--life-alive-color"),
        grid: color("--life-grid-color"),
        hover: color("--life-accent-color"),
      },
    );
    const viewportSize = renderer.resize();
    // The 1/2 expanded focus shifts the camera 1/6 of a viewport left
    // from its normal 1/3 focus.
    const cameraPadding = Math.ceil(viewportSize.width / 6);
    const engine = new NaiveLifeEngine(
      viewportSize.width + cameraPadding,
      viewportSize.height,
    );

    this.renderer = renderer;
    this.simulator = new LifeSimulator(engine);
    this.cameraFocus = {
      x: cameraPadding + viewportSize.width / 3,
      y: viewportSize.height / 2,
    };
    this.updateCamera();

    this.resizeObserver.observe(elements.canvas);

    const preset = this.selectStartupPreset();
    this.loadPreset(
      preset,
      this.hasAttribute("autoplay") && !window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches,
    );
    this.animationFrame = requestAnimationFrame(this.renderFrame);
  }

  disconnectedCallback(): void {
    cancelAnimationFrame(this.animationFrame);
    this.resizeObserver.disconnect();
    this.simulator = undefined;
    this.renderer = undefined;
    this.previousFrameAt = undefined;
    this.drawState = null;
  }

  attributeChangedCallback(): void {
    this.updateCamera();
  }

  private readonly renderFrame = (timestamp: number): void => {
    const simulator = this.simulator!;
    const renderer = this.renderer!;
    const elapsedMilliseconds = this.previousFrameAt === undefined
      ? 0
      : timestamp - this.previousFrameAt;
    this.previousFrameAt = timestamp;
    simulator.update(elapsedMilliseconds);

    renderer.render(simulator.snapshot(renderer.viewport));
    this.animationFrame = requestAnimationFrame(this.renderFrame);
  };

  private bindControls(): void {
    const elements = this.elements;

    elements.playButton.onclick = () =>
      this.setRunning(!this.simulator!.running);
    elements.clearButton.onclick = () => {
      this.simulator!.clear();
      this.updatePlayback();
      this.updatePatternDetails(
        "Blank canvas",
        "User-created",
        "Present",
        "An empty field ready for a new pattern.",
      );
    };
    elements.expandButton.onclick = () => this.setExpanded(!this.expanded);
    elements.speedInput.oninput = () => {
      const speed = Number(elements.speedInput.value);
      this.simulator!.setStepsPerSecond(speed);
      elements.speedValue.textContent = `${speed}/s`;
    };

    elements.canvas.onpointerdown = (event) => {
      const simulator = this.simulator!;
      const renderer = this.renderer!;
      const cell = renderer.cellAt(event.clientX, event.clientY);
      if (!cell) {
        return;
      }
      elements.canvas.setPointerCapture(event.pointerId);
      this.drawState = !simulator.getCell(cell.x, cell.y);
      simulator.setCell(cell.x, cell.y, this.drawState);
      renderer.setHoveredCell(cell);
    };
    elements.canvas.onpointermove = (event) => {
      const simulator = this.simulator!;
      const renderer = this.renderer!;
      const cell = renderer.cellAt(event.clientX, event.clientY);
      renderer.setHoveredCell(cell);
      if (this.drawState === null || !cell) {
        return;
      }
      simulator.setCell(cell.x, cell.y, this.drawState);
    };
    elements.canvas.onpointerup = this.finishDrawing;
    elements.canvas.onpointercancel = this.finishDrawing;
    elements.canvas.onpointerleave = () => {
      this.finishDrawing();
      this.renderer?.setHoveredCell(null);
    };
  }

  private readonly finishDrawing = (): void => {
    this.drawState = null;
  };

  private readonly resize = (): void => {
    this.renderer!.resize();
    this.updateCamera();
  };

  private updateCamera(): void {
    const center = Number(this.getAttribute("center") ?? DEFAULT_CENTER);
    this.renderer?.setCamera(
      this.cameraFocus,
      this.expanded ? 0.5 : center,
    );
  }

  private loadPreset(preset: LifePreset, autoplay: boolean): void {
    const simulator = this.simulator!;

    simulator.loadPattern(preset.rle, this.cameraFocus);
    simulator.setStepsPerSecond(preset.stepsPerSecond);
    this.elements.speedValue.textContent = `${simulator.stepsPerSecond}/s`;
    this.elements.speedInput.value = String(simulator.stepsPerSecond);
    this.updatePatternDetails(
      preset.name,
      preset.credit,
      preset.year,
      preset.description,
    );
    this.setRunning(autoplay);
  }

  private selectStartupPreset(): LifePreset {
    let previousId: string | null = null;
    try {
      previousId = sessionStorage.getItem(LAST_PRESET_KEY);
    } catch {
      // Storage can be unavailable in privacy-restricted browsing contexts.
    }

    const alternatives = FEATURED_PRESETS.filter(({ id }) => id !== previousId);
    const candidates = alternatives.length > 0 ? alternatives : FEATURED_PRESETS;
    if (candidates.length === 0) {
      throw new Error("At least one Life preset is required.");
    }
    const values = new Uint32Array(1);
    crypto.getRandomValues(values);
    const preset = candidates[(values[0] ?? 0) % candidates.length];
    if (!preset) {
      throw new Error("At least one Life preset is required.");
    }

    try {
      sessionStorage.setItem(LAST_PRESET_KEY, preset.id);
    } catch {
      // Random selection still works without session storage.
    }
    return preset;
  }

  private setExpanded(expanded: boolean): void {
    const elements = this.elements;
    this.expanded = expanded;
    this.dataset.expanded = String(expanded);
    elements.expandButton.setAttribute("aria-expanded", String(expanded));
    const label = expanded ? "Exit expanded" : "Expand";
    elements.expandButton.setAttribute("aria-label", label);
    elements.expandButton.title = label;
    elements.expandIcon.textContent = expanded
      ? "fullscreen_exit"
      : "fullscreen";
    this.updateCamera();
  }

  private setRunning(running: boolean): void {
    this.simulator!.setRunning(running);
    this.updatePlayback();
  }

  private updatePatternDetails(
    name: string,
    creator: string,
    year: number | string,
    description: string,
  ): void {
    this.elements.learnMoreName.textContent = name;
    this.elements.learnMoreAttribution.textContent = `, ${creator}, ${year}`;
    this.elements.learnMoreDescription.textContent = description;
  }

  private updatePlayback(): void {
    const elements = this.elements;
    const running = this.simulator!.running;
    elements.playIcon.textContent = running ? "pause" : "play_arrow";
    elements.playButton.setAttribute("aria-pressed", String(running));
    const label = running ? "Pause" : "Play";
    elements.playButton.setAttribute("aria-label", label);
    elements.playButton.title = label;
  }
}
